// src/services/enterpriseSharing.js
// Frontend service for enterprise sharing functionality

import proxyapi from './ProxyApi';
import { getCurrentUserId } from './database';

/**
 * Check if current user has enterprise access
 * @returns {Promise<Object>} - Enterprise access information
 */
export const checkEnterpriseAccess = async () => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const response = await proxyapi.get('/api/v1/enterprise/check-access', {
      params: { userId }
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error checking enterprise access:', error);
    throw error;
  }
};

/**
 * Get all enterprise users that can be shared with
 * @returns {Promise<Array>} - Array of enterprise user objects
 */
export const getEnterpriseUsers = async () => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const response = await proxyapi.get('/api/v1/enterprise/users', {
      params: { userId }
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data.users || [];
  } catch (error) {
    console.error('Error getting enterprise users:', error);
    throw error;
  }
};

/**
 * Share an item with enterprise users
 * @param {string} itemType - 'view' or 'flow'
 * @param {string} itemId - ID of the item to share
 * @param {Array<string>} emails - Array of email addresses to share with
 * @param {Object} options - Sharing options (password, expirationDays, versionId)
 * @returns {Promise<Object>} - Share response
 */
export const shareWithEnterpriseUsers = async (itemType, itemId, emails, options = {}) => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    if (!itemType || !itemId || !emails || emails.length === 0) {
      throw new Error('Item type, item ID, and email addresses are required');
    }
    
    const requestData = {
      itemType,
      itemId,
      sharedWithEmails: emails,
      userId,
      ...options
    };
    
    const response = await proxyapi.post('/api/v1/enterprise/share', requestData);
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error sharing with enterprise users:', error);
    throw error;
  }
};

/**
 * Get all items shared with the current user, with pagination, search, and sorting.
 * @param {number} page - The page number to retrieve.
 * @param {number} limit - The number of items per page.
 * @param {string} searchTerm - The term to search for.
 * @param {string} sortOption - The sorting option.
 * @returns {Promise<Object>} - An object containing shared items and total count.
 */
export const getSharedWithMe = async (page = 1, limit = 10, searchTerm = '', sortOption = 'dateDesc') => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const params = { userId, page, limit, sort: sortOption };
    if (searchTerm) {
        params.search = searchTerm;
    }
    
    const response = await proxyapi.get('/api/v1/enterprise/shared-with-me', { params });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data || { items: [], totalItems: 0 };
  } catch (error) {
    console.error('Error getting shared items:', error);
    throw error;
  }
};

/**
 * Copy a shared item to user's projects
 * @param {string} shareId - ID of the share to copy
 * @param {string} targetProjectId - ID of existing project (optional)
 * @param {string} newProjectTitle - Title for new project (optional)
 * @returns {Promise<Object>} - Copy response with project and view IDs
 */
export const copySharedItem = async (shareId, targetProjectId = null, newProjectTitle = null) => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    if (!shareId) {
      throw new Error('Share ID is required');
    }
    
    if (!targetProjectId && !newProjectTitle) {
      throw new Error('Either target project ID or new project title must be provided');
    }
    
    const requestData = {
      shareId,
      userId
    };
    
    if (targetProjectId) {
      requestData.targetProjectId = targetProjectId;
    }
    
    if (newProjectTitle) {
      requestData.newProjectTitle = newProjectTitle;
    }
    
    const response = await proxyapi.post('/api/v1/enterprise/copy-shared', requestData);
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error copying shared item:', error);
    throw error;
  }
};

/**
 * Get analytics for shares created by the current user
 * @returns {Promise<Object>} - Analytics data
 */
export const getShareAnalytics = async () => {
  try {
    const userId = await getCurrentUserId();
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const response = await proxyapi.get('/api/v1/enterprise/share-analytics', {
      params: { userId }
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data.analytics;
  } catch (error) {
    console.error('Error getting share analytics:', error);
    throw error;
  }
};

/**
 * Check if user is part of an enterprise (simple check)
 * @returns {Promise<boolean>} - True if user has enterprise access
 */
export const isEnterpriseUser = async () => {
  try {
    const accessInfo = await checkEnterpriseAccess();
    return accessInfo.hasEnterpriseAccess;
  } catch (error) {
    console.error('Error checking if user is enterprise user:', error);
    return false;
  }
};

/**
 * Validate email addresses
 * @param {Array<string>} emails - Array of email addresses
 * @returns {Object} - Validation result with valid and invalid emails
 */
export const validateEmails = (emails) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const valid = [];
  const invalid = [];
  
  emails.forEach(email => {
    const trimmedEmail = email.trim().toLowerCase();
    if (emailRegex.test(trimmedEmail)) {
      valid.push(trimmedEmail);
    } else {
      invalid.push(email);
    }
  });
  
  return { valid, invalid };
};

/**
 * Format sharing options for API call
 * @param {Object} options - Raw options from form
 * @returns {Object} - Formatted options for API
 */
export const formatSharingOptions = (options) => {
  const formatted = {};
  
  if (options.password && options.password.trim()) {
    formatted.password = options.password.trim();
  }
  
  if (options.expiration && options.expiration !== 'never') {
    formatted.expirationDays = parseInt(options.expiration, 10);
  }
  
  if (options.versionId) {
    formatted.versionId = options.versionId;
  }
  
  return formatted;
};

/**
 * Enterprise sharing service object
 * Provides all enterprise sharing functionality
 */
const enterpriseSharingService = {
  checkEnterpriseAccess,
  getEnterpriseUsers,
  shareWithEnterpriseUsers,
  getSharedWithMe,
  copySharedItem,
  getShareAnalytics,
  isEnterpriseUser,
  validateEmails,
  formatSharingOptions
};

export default enterpriseSharingService;